import { httpClient, readEventStream, ReadService, type HttpClient } from '../core'
import { sseTasksEndpoints } from './sse-tasks.endpoints'
import { isTerminal, sseTaskPageSchema, sseTaskSchema, taskPhase, type SseTaskDto, type SseTaskFilters } from './sse-tasks.schemas'

/** Reads the backend's background tasks (list, one), and follows one live. Tasks are made by other calls (an upload), never directly. */
export class SseTasksService extends ReadService<SseTaskDto, SseTaskFilters> {
  constructor(http: HttpClient = httpClient) {
    super(http, sseTasksEndpoints, { item: sseTaskSchema, page: sseTaskPageSchema })
  }

  /**
   * Follows one task: yields its state each time the server pushes it, and ends once it is done or failed (or the server closes the
   * stream, or `signal` aborts). A frame that is not a task is skipped rather than ending the stream.
   */
  async *stream(taskId: string, signal?: AbortSignal): AsyncGenerator<SseTaskDto> {
    const response = await this.http.open(sseTasksEndpoints.events(taskId), { signal })
    for await (const frame of readEventStream(response)) {
      let json: unknown
      try {
        json = JSON.parse(frame.data)
      } catch {
        // A frame that is only a word ("completed", "done") still says how the task went.
        json = /^\s*[\w -]+\s*$/.test(frame.data) ? { status: frame.data.trim() } : null
      }
      if (!json || typeof json !== 'object') continue
      // Frames often carry only what changed, not the task's id; the stream is for this one task, so the id is known. The event's own
      // name ("progress", "completed", "error") fills in a status the data left out.
      const named = /error|fail/i.test(frame.event) ? { status: 'Failed' } : /complete|done|finish|success/i.test(frame.event) ? { status: 'Completed', progress: 100 } : {}
      const parsed = sseTaskSchema.safeParse({ id: taskId, ...named, ...(json as Record<string, unknown>) })
      if (!parsed.success) continue
      yield parsed.data
      if (isTerminal(taskPhase(parsed.data))) return
    }
  }
}

export const sseTasksService = new SseTasksService()
